import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const LifeMovieTimeline = ({ birthYear = 1997, currentAge = 27 }) => {
  const scrollContainerRef = useRef(null);
  
  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Generate life movie cards
  const generateLifeCards = () => {
    const cards = [];
    const currentYear = new Date().getFullYear();
    const endYear = birthYear + 80; // Show up to age 80
    
    for (let year = birthYear; year <= endYear; year++) {
      const age = year - birthYear;
      const isPast = year < currentYear;
      const isCurrent = age === currentAge;
      const isFutureNear = age > currentAge && age <= currentAge + 5;
      const isFutureFar = age > currentAge + 5;
      
      // Define theme colors by life phase
      let themeColor = '#e7e5e4'; // default gray
      let phaseLabel = '';
      
      if (age <= 12) {
        themeColor = isPast ? '#bae6fd' : '#e0f2fe'; // sky blue
        phaseLabel = 'Childhood';
      } else if (age <= 25) {
        themeColor = isPast ? '#c7d2fe' : '#e0e7ff'; // indigo
        phaseLabel = 'Youth';
      } else if (age <= 40) {
        themeColor = isPast ? '#fcd34d' : '#fef3c7'; // yellow
        phaseLabel = 'Prime';
      } else if (age <= 60) {
        themeColor = isPast ? '#fdba74' : '#fed7aa'; // orange
        phaseLabel = 'Maturity';
      } else {
        themeColor = isPast ? '#c4b5fd' : '#ede9fe'; // purple
        phaseLabel = 'Wisdom';
      }

      // Key life events (sample data)
      const events = [];
      if (age === 0) events.push({ domain: 'birth', text: 'Born', color: '#10b981' });
      if (age === 18) events.push({ domain: 'education', text: 'College', color: '#6366f1' });
      if (age === 23) events.push({ domain: 'career', text: 'Career Start', color: '#f59e0b' });
      if (age === 29) events.push({ domain: 'relationships', text: 'Relationship Peak', color: '#ec4899' });
      if (age === 35) events.push({ domain: 'health', text: 'Health Focus', color: '#ef4444' });
      if (age === 42) events.push({ domain: 'wealth', text: 'Wealth Boom', color: '#8b5cf6' });
      if (age === 50) events.push({ domain: 'spirituality', text: 'Spiritual Awakening', color: '#d946ef' });

      cards.push({
        year,
        age,
        isPast,
        isCurrent,
        isFutureNear,
        isFutureFar,
        themeColor,
        phaseLabel,
        events,
      });
    }
    
    return cards;
  };

  const lifeCards = generateLifeCards();

  return (
    <div className="relative">
      {/* Navigation buttons */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
        <button
          onClick={() => scroll('left')}
          className="p-2 bg-white rounded-full shadow-card hover:shadow-card-hover transition-all"
        >
          <ChevronLeft className="w-5 h-5 text-stone-700" />
        </button>
      </div>
      <div className="absolute right-0 top-1/2 -translate-y-1/2 z-10">
        <button
          onClick={() => scroll('right')}
          className="p-2 bg-white rounded-full shadow-card hover:shadow-card-hover transition-all"
        >
          <ChevronRight className="w-5 h-5 text-stone-700" />
        </button>
      </div>

      {/* Timeline container */}
      <div
        ref={scrollContainerRef}
        className="flex gap-4 overflow-x-auto pb-4 px-12 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {lifeCards.map((card) => (
          <div
            key={card.year}
            className={`
              flex-shrink-0 w-48 rounded-card p-5 transition-all duration-300
              ${card.isCurrent ? 'shadow-cosmic scale-105' : 'shadow-card hover:shadow-card-hover'}
              ${card.isFutureFar ? 'opacity-60' : 'opacity-100'}
            `}
            style={{ backgroundColor: card.themeColor }}
          >
            {/* Header */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-2xl font-serif font-medium text-stone-900">
                  {card.age}
                </span>
                {card.isCurrent && (
                  <span className="px-2 py-0.5 bg-cosmic-500 text-white text-xs rounded-full">
                    Now
                  </span>
                )}
              </div>
              <div className="text-xs text-stone-600">
                {card.year} · {card.phaseLabel}
              </div>
            </div>

            {/* Events */}
            {card.events.length > 0 && (
              <div className="space-y-2">
                {card.events.map((event, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 p-2 bg-white/50 rounded-lg backdrop-blur-sm"
                  >
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: event.color }}
                    />
                    <span className="text-xs font-medium text-stone-900">
                      {event.text}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state for cards without events */}
            {card.events.length === 0 && !card.isPast && (
              <div className="h-16 flex items-center justify-center">
                <span className="text-xs text-stone-500">
                  {card.isFutureNear ? 'Upcoming' : 'Future'}
                </span>
              </div>
            )}

            {/* Fate node indicator */}
            {card.events.length > 0 && (
              <div className="mt-3 pt-3 border-t border-stone-900/10">
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-sacred-400 animate-pulse" />
                  <span className="text-xs text-stone-700">Fate Node</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* CSS to hide scrollbar */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default LifeMovieTimeline;